import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { aiCreditsService, AiCreditSummary, AiEstimatesTable, CANAL_RECARGA } from '../services/aiCreditsService';
import AiRechargeModal from '../components/ai/AiRechargeModal';

/**
 * Saldo de IA e médias por ação, compartilhados por todos os selos de custo da tela (uma busca
 * só) e a janela de recarga. A janela abre por cima de qualquer tela, sem trocar de página, então
 * o que a pessoa estava escrevendo continua ali. O saldo atualiza ao voltar para a aba e quando a
 * aba do pagamento avisa que a recarga entrou.
 */
interface AiCreditsContextValue {
  summary: AiCreditSummary | null;
  estimates: AiEstimatesTable | null;
  refresh: () => Promise<void>;
  openRecharge: () => void;
}

const AiCreditsContext = createContext<AiCreditsContextValue>({
  summary: null,
  estimates: null,
  refresh: async () => {},
  openRecharge: () => {}
});

export const AiCreditsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [summary, setSummary] = useState<AiCreditSummary | null>(null);
  const [estimates, setEstimates] = useState<AiEstimatesTable | null>(null);
  const [rechargeOpen, setRechargeOpen] = useState(false);
  const ultimaBusca = useRef(0);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) return;
    ultimaBusca.current = Date.now();
    try {
      setSummary(await aiCreditsService.summary());
    } catch { /* sem saldo na tela; a IA continua funcionando */ }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) { setSummary(null); setEstimates(null); return; }
    refresh();
    aiCreditsService.estimates().then(setEstimates).catch(() => { /* usa o padrão */ });
  }, [isAuthenticated, refresh]);

  // Voltou para a aba (por exemplo, depois de pagar na aba do Stripe): atualiza o saldo
  useEffect(() => {
    const aoVoltar = () => {
      if (document.visibilityState === 'visible' && Date.now() - ultimaBusca.current > 5000) refresh();
    };
    window.addEventListener('focus', aoVoltar);
    document.addEventListener('visibilitychange', aoVoltar);
    let canal: BroadcastChannel | null = null;
    try {
      canal = new BroadcastChannel(CANAL_RECARGA);
      canal.onmessage = () => refresh();
    } catch { /* navegador sem BroadcastChannel: fica o foco */ }
    // Gasto do mês muda a cada uso de IA: atualiza de tempos em tempos enquanto a aba está aberta
    const intervalo = window.setInterval(() => { if (document.visibilityState === 'visible') refresh(); }, 60000);
    return () => {
      window.removeEventListener('focus', aoVoltar);
      document.removeEventListener('visibilitychange', aoVoltar);
      window.clearInterval(intervalo);
      if (canal) canal.close();
    };
  }, [refresh]);

  const openRecharge = useCallback(() => setRechargeOpen(true), []);

  // Uma ação de IA foi barrada por falta de crédito (402): atualiza o saldo e abre a recarga
  useEffect(() => {
    const semCredito = () => { refresh(); setRechargeOpen(true); };
    window.addEventListener('devskin:ai-no-credits', semCredito);
    return () => window.removeEventListener('devskin:ai-no-credits', semCredito);
  }, [refresh]);

  return (
    <AiCreditsContext.Provider value={{ summary, estimates, refresh, openRecharge }}>
      {children}
      <AiRechargeModal open={rechargeOpen} onClose={() => setRechargeOpen(false)} summary={summary} onRefresh={refresh} />
    </AiCreditsContext.Provider>
  );
};

export const useAiCredits = () => useContext(AiCreditsContext);
